import React, { useState, useEffect, useCallback } from "react"
import { noop } from "../../../../lib/misc/noop"

// Define the available permissions
const availablePermissions = {
    siren_config: ["read"],
    siren_resources: ["read"],
    siren_alert: ["read"],
    contacts: ["read"],
} as const

type PermissionCategory = keyof typeof availablePermissions

type PermissionsProps = {
    initialValue?: string[]
    onChange?: (value: string[]) => void
}

const wordMap: Record<string, string> = {
    "full_access": "Full Access",
    "read": "View Only",
} as const

const categoryLabel: Record<string, string> = {
    "siren_config": "Siren Config",
    "siren_resources": "Siren Resources",
    "siren_alert": "Siren Alert",
    "contacts": "Contacts",
}

export function PermissionsEditor({
                                      initialValue = [],
                                      onChange = noop,
                                  }: PermissionsProps) {

    // Convert from ["contacts:read", "siren:full_access"] → { contacts: ["read"], siren: ["full_access"] }
    const [values, setValues] = useState<Record<string, string[]>>(() => {
        const map: Record<string, string[]> = {}
        initialValue.forEach(item => {
            const [category, perm] = item.split(":")
            if (!category || !perm) return
            if (!map[category]) map[category] = []
            map[category].push(perm)
        })
        return map
    })

    const handleChange = useCallback((category: string, perms: string[]) => {
        setValues(prev => {
            const updated = { ...prev, [category]: perms }

            // Flatten back to ["contacts:read", "siren:full_access"]
            const flattened = Object.entries(updated).flatMap(([cat, ps]) =>
                ps.map(p => `${cat}:${p}`)
            )
            onChange(flattened)
            return updated
        })
    }, [onChange])

    return (
        <div className="space-y-3">
            <label className="block mb-2">Permissions</label>

            {(Object.keys(availablePermissions) as (keyof typeof availablePermissions)[]).map(category => (
                <Row
                    key={category}
                    category={category}
                    available={[...availablePermissions[category], "full_access"]}
                    initialValue={values[category] || []}
                    onChange={handleChange}
                />
            ))}
        </div>
    )
}

type RowProps = {
    category: PermissionCategory
    available: string[]
    initialValue: string[]
    onChange: (key: PermissionCategory, permissions: string[]) => void
}

function Row({ category, available, initialValue, onChange }: RowProps) {
    const [selected, setSelected] = useState<string[]>(initialValue)

    const toggle = (perm: string) => {
        setSelected(prev => {
            let updated: string[]

            if (perm === "full_access") {
                // full_access overrides everything
                updated = prev.includes("full_access") ? [] : ["full_access"]
            } else {
                // deselect full_access if other perms are selected
                const base = prev.filter(p => p !== "full_access")
                updated = base.includes(perm)
                    ? base.filter(p => p !== perm)
                    : [...base, perm]
            }

            onChange(category, updated)
            return updated
        })
    }

    useEffect(() => {
        if (initialValue.join(",") !== selected.join(",")) {
            setSelected(initialValue)
        }
    }, [initialValue, selected])

    const hasFullAccess = selected.includes("full_access")

    return (
        <div className="flex items-center space-x-3">
            <div className="w-32 font-medium">{categoryLabel[category] ?? category}</div>
            <div className="flex flex-grow shadow-md rounded-lg overflow-hidden">
                {available.map((perm, i) => {
                    const active = hasFullAccess || selected.includes(perm)
                    const isFirst = i === 0

                    return (
                        <button
                            key={perm}
                            type="button"
                            onClick={() => toggle(perm)}
                            className={`
                                px-3 py-1 text-sm border-0 flex-grow hover:opacity-80
                                ${active
                                ? "bg-blue text-white"
                                : "bg-light-light-grey text-grey"}
                                ${!isFirst ? "border border-l-1 border-white" : ""}
                            `}
                        >
                            {wordMap[perm] ? wordMap[perm] : perm}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
